import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import translations from 'services/translations/translations';
import NumberInputField from 'components/layout/forms/NumberInputField';
import IWall from 'components/interfaces/IWall';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { setWall } from 'store/selectedWall/actions';
import { useDispatch } from 'react-redux';
import TrashIcon from 'components/layout/buttons/TrashIcon';
import ChevronDownIcon from 'components/layout/buttons/ChevronDownIcon';
import ChevronUpIcon from 'components/layout/buttons/ChevronUpIcon';
import TranslatedOption from 'components/layout/forms/TranslatedOption';
import CheckboxInputField from 'components/layout/forms/CheckboxInputField';

const ProfileSplits = ({
  wall,
  disableInputs,
}: {
  wall: IWall;
  disableInputs?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.glassWallProperties]);
  const dispatch = useDispatch();

  const colors = window.GetPreviewSplitColors();

  const [relativeMode, setRelativeMode] = useState(true);
  const [splits, setSplits] = useState<number[]>([]);
  // NOTE: See note below
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [profileType, setProfileType] = useState<'Roof' | 'Floor'>('Roof');

  useEffect(() => {
    if (relativeMode) {
      setSplits(splits.slice().sort((a, b) => a - b));
    }
    // We only want to sort the splits if we are switching to relativeMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relativeMode]);

  useEffect(() => {
    if (profileType === 'Roof') {
      setSplits(wall.roofProfileSplits);
    } else if (profileType === 'Floor') {
      setSplits(wall.floorProfileSplits);
    }

    if (wall.wallID !== undefined) {
      // Isn't wallID a string???
      window.EnablePreviewSplits(wall.wallID.toString(), splits);
    }

    // Disabling react-hooks/exhaustive-deps since depending on `wall`
    // would result in splits being set all the time, but this hook
    // should only run when the component is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileType]);

  useEffect(() => {
    const sortedSplits = splits.slice().sort((a, b) => a - b);

    dispatch(
      setWall({
        ...wall,
        roofProfileSplits:
          profileType === 'Roof' ? sortedSplits : wall.roofProfileSplits,
        floorProfileSplits:
          profileType === 'Floor' ? sortedSplits : wall.floorProfileSplits,
      })
    );

    if (wall.wallID !== undefined) {
      window.EnablePreviewSplits(wall.wallID.toString(), splits);
    }
    // Disabling react-hooks/exhaustive-deps since depending on `wall`
    // would result in infinite loop since setWall changes wall
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits]);

  const relativeValueFor = (index: number) => {
    const currentValue = splits[index];
    let before = 0;
    for (let i = 0; i < splits.length; i++) {
      if (splits[i] > before && splits[i] < currentValue) {
        before = splits[i];
      }
    }

    return currentValue - before;
  };

  const absoluteValueFor = (index: number) => {
    return splits[index];
  };

  const alternativeValueFor = (index: number) => {
    if (relativeMode) return absoluteValueFor(index);
    return relativeValueFor(index);
  };

  const valueFor = (index: number) => {
    if (relativeMode) return relativeValueFor(index);
    return absoluteValueFor(index);
  };

  const updateValue = (index: number, newValue: number) => {
    if (relativeMode) {
      const oldValue = valueFor(index);
      changeByDiff(index, newValue - oldValue);
      return;
    }

    if (newValue < 0) newValue = 0;

    setSplits((previous) =>
      previous.map((item, i) => (i === index ? newValue : item))
    );

    if (wall.wallID !== undefined) {
      window.EnablePreviewSplits(wall.wallID.toString(), splits);
    }
  };

  const removeSplit = (index: number) => {
    setSplits((previous) => previous.filter((_, i) => i !== index));
  };

  const changeByDiff = (index: number, diff: number) => {
    setSplits((previous) =>
      previous.map((item, i) =>
        (relativeMode ? i >= index : i === index) ? previous[i] + diff : item
      )
    );
  };

  const getDropDownOptions = () => {
    return [
      <TranslatedOption
        translationFile={translations.common}
        element="Roof"
        section=""
        key="Roof"
      />,
      <TranslatedOption
        translationFile={translations.common}
        element="Floor"
        section=""
        key="Floor"
      />,
    ];
  };

  // NOTE: Add this code at the beginning of the div to enable
  //       selecting between roof and floor profiles.
  //       This is currently disabled since we thought Harri would want this now
  //       but that wasn't the case, he will want it in the future and when he does
  //       we'll just enable it. All logic is already done
  // <SelectInputFieldControlled
  //   id="profileType"
  //   label={t(`${translations.common}:Profile Type`)}
  //   onChange={(event) => {
  //     if (event.target.value === 'Roof' || event.target.value === 'Floor') {
  //       setProfileType(event.target.value);
  //     }
  //   }}
  //   name="profileType"
  //   required
  //   options={getDropDownOptions()}
  //   value={profileType}
  //   disabled={disableInputs}
  // />
  return (
    <div>
      <CheckboxInputField
        id="relativeMode"
        label={t(`${translations.common}:Relative`)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setRelativeMode(e.target.checked)
        }
        value={relativeMode}
        name="relativeMode"
        required
      />
      <Row>
        <Col>
          <div
            style={{
              backgroundColor: 'transparent',
              borderRadius: '4px',
              width: '42.8px',
            }}
          />
        </Col>
        <Col sm={7} className="label">
          {relativeMode
            ? t(`${translations.common}:Relative`)
            : t(`${translations.common}:Absolute`)}
        </Col>
        <Col sm={2} style={{ textAlign: 'center' }}>
          {relativeMode
            ? t(`${translations.common}:Absolute`)
            : t(`${translations.common}:Relative`)}
        </Col>
        <Col />
        <Col />
      </Row>
      {splits.map((_, index) => (
        <Row key={index}>
          <Col>
            <div
              style={{
                backgroundColor: `#${colors[index % colors.length]
                  .toString(16)
                  .padStart(6, '0')}`,
                borderRadius: '4px',
                height: '42.8px',
                aspectRatio: '1 / 1',
              }}
            />
          </Col>
          <Col sm={7}>
            <NumberInputField
              id={`profile-split${index}`}
              onChange={(newValue: {
                target: { name: string; value: number };
              }) => updateValue(index, newValue.target.value)}
              value={valueFor(index)}
              name={`profile-split${index}`}
              required
              disabled={disableInputs}
            />
          </Col>
          <Col
            sm={2}
            style={{ textAlign: 'center' }}
            className="modal-delete-button ml-0"
          >
            {alternativeValueFor(index)}
          </Col>
          <Col>
            <ChevronUpIcon onClick={() => changeByDiff(index, 1)} />
            <ChevronDownIcon onClick={() => changeByDiff(index, -1)} />
          </Col>
          <Col className="">
            <TrashIcon
              className="modal-delete-button ml-0"
              onClick={() => removeSplit(index)}
            />
          </Col>
        </Row>
      ))}
      <PrimaryButton
        className="action-button mr-4"
        onClick={() => {
          setSplits([
            ...splits,
            splits.length > 0 ? splits[splits.length - 1] + 500 : 1000,
          ]);
        }}
      >
        {t(`${translations.common}:Add split`).toLocaleUpperCase()}
      </PrimaryButton>

      <PrimaryButton
        className="action-button"
        onClick={() => {
          setSplits(splits.slice().sort((a, b) => a - b));
        }}
      >
        {t(`${translations.common}:Sort`).toLocaleUpperCase()}
      </PrimaryButton>
    </div>
  );
};

export default ProfileSplits;
